import { Pause, Play } from 'lucide-react'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import { formatOffset } from '../format'
import { CARD, MONO } from '../recipes'
import type { SessionBookmark } from '../types'
import { bookmarkLabelText } from './questioning-format'
import type { PlaybackState } from './questioning-types'

export interface AudioScrubberProps {
  playback: PlaybackState
  /** Loudness of the whole session, 0..1 per bar. */
  waveform: readonly number[]
  bookmarks: readonly SessionBookmark[]
  actions: { onTogglePlay: () => void; onSeek: (ms: number) => void }
}

/**
 * Session player (SC-034): play / pause, a waveform scrubber with the bookmarks as copper
 * ticks, and "01:31 / 18:42". The waveform is a native range input underneath, so it seeks by
 * keyboard and screen reader too. Bookmark labels show from 448 px (own `@container`).
 */
export function AudioScrubber({ playback, waveform, bookmarks, actions }: AudioScrubberProps) {
  const { t } = useTranslation()
  const position = formatOffset(playback.positionMs)
  const duration = formatOffset(playback.durationMs)
  return (
    <section
      aria-label={t('humint.questioning.player.title')}
      className={cn(CARD, '@container flex items-center gap-3 p-3 @5xl/humint:p-4')}
    >
      <Button
        variant="secondary"
        size="icon"
        className="size-12 shrink-0 rounded-xl @5xl/humint:size-10"
        onClick={actions.onTogglePlay}
        aria-label={
          playback.playing
            ? t('humint.questioning.player.pause')
            : t('humint.questioning.player.play')
        }
      >
        {playback.playing ? (
          <Pause aria-hidden className="size-5 fill-current" />
        ) : (
          <Play aria-hidden className="size-5 fill-current" />
        )}
      </Button>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <Waveform
          playback={playback}
          waveform={waveform}
          bookmarks={bookmarks}
          onSeek={actions.onSeek}
        />
        <p className={cn(MONO, 'text-xs text-muted-foreground')}>
          <span className="font-semibold text-foreground">{position}</span> / {duration}
        </p>
      </div>
    </section>
  )
}

interface WaveformProps {
  playback: PlaybackState
  waveform: readonly number[]
  bookmarks: readonly SessionBookmark[]
  onSeek: (ms: number) => void
}

/** Horizontal position of an offset on the waveform, as a CSS percentage. */
const offsetLeft = (ms: number, durationMs: number) => `${(ms / Math.max(1, durationMs)) * 100}%`

/** The invisible native range input over the bars: keyboard and screen-reader seeking. */
function SeekInput({ playback, onSeek }: Pick<WaveformProps, 'playback' | 'onSeek'>) {
  const { t } = useTranslation()
  return (
    <input
      type="range"
      min={0}
      max={playback.durationMs}
      step={1000}
      value={playback.positionMs}
      onChange={event => onSeek(Number(event.target.value))}
      aria-label={t('humint.questioning.player.seek')}
      aria-valuetext={t('humint.questioning.player.position', {
        position: formatOffset(playback.positionMs),
        duration: formatOffset(playback.durationMs),
      })}
      className="peer absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
    />
  )
}

/**
 * The bars twice: a muted layer and a copper one on top, clipped to the played part. Both
 * layers are memoised on the waveform, so a playback tick only moves the clip edge.
 */
function WaveBars({ playback, waveform }: Pick<WaveformProps, 'playback' | 'waveform'>) {
  const played =
    playback.durationMs > 0 ? Math.min(1, playback.positionMs / playback.durationMs) : 0
  return (
    <div
      aria-hidden
      className="relative h-10 rounded-md peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-card"
    >
      <BarLayer waveform={waveform} tone="bg-muted-foreground/60" />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${(1 - played) * 100}% 0 0)` }}>
        <BarLayer waveform={waveform} tone="bg-primary" />
      </div>
    </div>
  )
}

const BarLayer = memo(function BarLayer({
  waveform,
  tone,
}: {
  waveform: readonly number[]
  tone: string
}) {
  return (
    <div className="flex h-full items-center gap-px">
      {waveform.map((level, index) => (
        <span
          key={index}
          style={{ height: `${Math.round(Math.max(0.1, level) * 100)}%` }}
          className={cn('min-w-0 flex-1 rounded-full', tone)}
        />
      ))}
    </div>
  )
})

/** Bookmark labels under the bars, from 448 px only (own `@container`). */
function BookmarkLabels({
  bookmarks,
  durationMs,
}: {
  bookmarks: readonly SessionBookmark[]
  durationMs: number
}) {
  const { t } = useTranslation()
  return (
    <div aria-hidden className="relative hidden h-4 @md:block">
      {bookmarks
        .filter(bookmark => bookmark.label)
        .map(bookmark => (
          <span
            key={bookmark.id}
            style={{ left: offsetLeft(bookmark.atMs, durationMs) }}
            className="absolute top-0 text-2xs leading-4 whitespace-nowrap text-primary-strong"
          >
            {bookmarkLabelText(bookmark.label, t)}{' '}
            <span className={MONO}>{formatOffset(bookmark.atMs)}</span>
          </span>
        ))}
    </div>
  )
}

function Waveform({ playback, waveform, bookmarks, onSeek }: WaveformProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="relative">
        <SeekInput playback={playback} onSeek={onSeek} />
        <WaveBars playback={playback} waveform={waveform} />
        <div aria-hidden className="pointer-events-none absolute inset-0">
          {bookmarks.map(bookmark => (
            <span
              key={bookmark.id}
              style={{ left: offsetLeft(bookmark.atMs, playback.durationMs) }}
              className="absolute -top-1 -bottom-1 w-0.5 rounded-full bg-primary-strong"
            />
          ))}
        </div>
      </div>
      <BookmarkLabels bookmarks={bookmarks} durationMs={playback.durationMs} />
    </div>
  )
}
